import { useState, useEffect } from "react";
import { toast } from "sonner";
import { DollarSign, Clock, CheckCircle, Users, Search, CreditCard, X, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminBackfillCommissions } from "@/api";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminCommissions() {
  const [summary, setSummary] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [filteredCommissions, setFilteredCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCommissions, setSelectedCommissions] = useState([]);
  const [paymentRef, setPaymentRef] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [showPayModal, setShowPayModal] = useState(false);
  const [currentCommission, setCurrentCommission] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const handleBackfill = async () => {
    if (!window.confirm(
      "Scan all successful subscriptions and auto-create missing commission records for agent-referred customers?\n\nSafe to re-run — existing commissions are skipped."
    )) return;
    setBackfilling(true);
    try {
      const res = await adminBackfillCommissions();
      const s = res.data.summary;
      toast.success(
        `Backfill complete: created ${s.created} commission(s). ` +
        `Scanned ${s.scanned}, skipped ${s.skipped_existing} existing, ${s.skipped_no_agent} without agent.`
      );
      await loadCommissions();
    } catch (e) {
      toast.error("Backfill failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBackfilling(false);
    }
  };

  useEffect(() => { loadCommissions(); }, [filterStatus]);
  useEffect(() => { filterCommissionsData(); }, [commissions, searchTerm, filterStatus]);

  const loadCommissions = async () => {
    try {
      const token = localStorage.getItem("rebuilt_token");
      const url = filterStatus !== "all" ? `${API_URL}/api/admin/commissions?status=${filterStatus}` : `${API_URL}/api/admin/commissions`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error("Failed");
      const data = await response.json();
      setSummary(data.summary);
      setCommissions(data.commissions);
    } catch (error) {
      toast.error("Failed to load commissions");
    } finally { setLoading(false); }
  };

  const filterCommissionsData = () => {
    let filtered = commissions;
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.agent?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.agent?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.customer?.mobile?.includes(searchTerm)
      );
    }
    setFilteredCommissions(filtered);
  };

  const handlePaySingle = (commission) => {
    setCurrentCommission(commission);
    setShowPayModal(true);
    setPaymentRef(""); setPaymentNotes("");
  };

  const handlePayBulk = () => {
    if (selectedCommissions.length === 0) { toast.error("Select at least one commission"); return; }
    setCurrentCommission(null);
    setShowPayModal(true);
    setPaymentRef(""); setPaymentNotes("");
  };

  const confirmPayment = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem("rebuilt_token");
      if (currentCommission) {
        const response = await fetch(`${API_URL}/api/admin/commission/${currentCommission.id}/pay`, {
          method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ payment_ref: paymentRef, notes: paymentNotes })
        });
        if (!response.ok) throw new Error("Failed");
        toast.success("Commission paid successfully!");
      } else {
        const response = await fetch(`${API_URL}/api/admin/commissions/pay-bulk`, {
          method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ commission_ids: selectedCommissions, payment_ref: paymentRef })
        });
        if (!response.ok) throw new Error("Failed");
        const data = await response.json();
        toast.success(`${data.paid_count} commissions paid successfully!`);
        setSelectedCommissions([]);
      }
      setShowPayModal(false);
      loadCommissions();
    } catch { toast.error("Payment failed"); } finally { setProcessing(false); }
  };

  const toggleSelectCommission = (commId) => {
    setSelectedCommissions(prev => prev.includes(commId) ? prev.filter(id => id !== commId) : [...prev, commId]);
  };

  const toggleSelectAll = () => {
    const pendingIds = filteredCommissions.filter(c => c.status === 'pending').map(c => c.id);
    setSelectedCommissions(selectedCommissions.length === pendingIds.length ? [] : pendingIds);
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-3 border-[#1565C0]/20 border-t-[#1565C0] rounded-full animate-spin" style={{borderWidth: '3px'}} />
        <p className="text-sm text-[#6B7280]">Loading commissions...</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Outfit, sans-serif'}}>Commission Management</h2>
          <p className="text-sm text-[#6B7280] mt-1">Manage agent commission payments</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <a href={`${API_URL}/api/admin/export/commissions/csv`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all"
            onClick={(e) => {
              e.preventDefault();
              const token = localStorage.getItem("rebuilt_token");
              fetch(`${API_URL}/api/admin/export/commissions/csv`, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => res.blob())
                .then(blob => { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'commissions_report.csv'; a.click(); URL.revokeObjectURL(url); })
                .catch(() => toast.error("Export failed"));
            }}>
            Export CSV
          </a>
          <a href={`${API_URL}/api/admin/export/commissions/pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all"
            onClick={(e) => {
              e.preventDefault();
              const token = localStorage.getItem("rebuilt_token");
              fetch(`${API_URL}/api/admin/export/commissions/pdf`, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => res.blob())
                .then(blob => { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'commissions_report.pdf'; a.click(); URL.revokeObjectURL(url); })
                .catch(() => toast.error("Export failed"));
            }}>
            Export PDF
          </a>
          {selectedCommissions.length > 0 && (
            <Button onClick={handlePayBulk} className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl px-5 py-2.5 font-semibold shadow-sm hover:shadow-md transition-all">
              <CreditCard className="h-4 w-4 mr-2" /> Pay {selectedCommissions.length} Selected
            </Button>
          )}
          <Button
            onClick={handleBackfill}
            disabled={backfilling}
            variant="outline"
            className="rounded-xl px-5 py-2.5 font-semibold border-[#1565C0]/30 text-[#1565C0] hover:bg-[#1565C0]/5"
            data-testid="backfill-commissions-btn"
            title="Auto-create missing commission records for historical subscription payments"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${backfilling ? 'animate-spin' : ''}`} />
            {backfilling ? 'Scanning…' : 'Backfill Commissions'}
          </Button>
        </div>
      </div>

      {/* Stats */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Commissions', value: formatCurrency(summary.total_commissions), icon: DollarSign, color: '#3B82F6', bg: 'bg-blue-50' },
            { label: `Pending (${summary.pending_count})`, value: formatCurrency(summary.pending_amount), icon: Clock, color: '#F59E0B', bg: 'bg-amber-50' },
            { label: `Paid (${summary.paid_count})`, value: formatCurrency(summary.paid_amount), icon: CheckCircle, color: '#10B981', bg: 'bg-emerald-50' },
            { label: 'Total Entries', value: (summary.pending_count || 0) + (summary.paid_count || 0), icon: Users, color: '#8B5CF6', bg: 'bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">{stat.label}</p>
                  <p className="text-2xl font-black text-[#0A2540] mt-1" style={{fontFamily:'Outfit, sans-serif'}}>{stat.value}</p>
                </div>
                <div className={`${stat.bg} p-3 rounded-xl`}>
                  <stat.icon className="h-6 w-6" style={{color: stat.color}} strokeWidth={1.8} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
            <input placeholder="Search by agent name, email, customer..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0] focus:outline-none focus:bg-white transition-all" />
          </div>
          <div className="flex gap-2">
            {["all", "pending", "paid"].map(status => (
              <button key={status} onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-xl text-xs font-semibold capitalize transition-all border ${
                  filterStatus === status ? 'bg-[#1565C0] text-white border-[#1565C0] shadow-sm' : 'bg-white text-[#6B7280] border-gray-200 hover:bg-gray-50'
                }`}>
                {status}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-50/80 border-b border-gray-100">
                <th className="px-4 py-4 text-left"><input type="checkbox" checked={selectedCommissions.length > 0 && selectedCommissions.length === filteredCommissions.filter(c => c.status === 'pending').length} onChange={toggleSelectAll} className="rounded border-gray-300" /></th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Agent</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Customer</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Ref</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredCommissions.length === 0 ? (
                <tr><td colSpan="8" className="px-6 py-16 text-center">
                  <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-[#6B7280] font-medium">No commissions found</p>
                </td></tr>
              ) : filteredCommissions.map(comm => (
                <tr key={comm.id} className="border-b border-gray-50 hover:bg-blue-50/30 transition-colors duration-150 group">
                  <td className="px-4 py-4">{comm.status === 'pending' && <input type="checkbox" checked={selectedCommissions.includes(comm.id)} onChange={() => toggleSelectCommission(comm.id)} className="rounded border-gray-300" />}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs shadow-sm">{(comm.agent?.name || 'A')[0].toUpperCase()}</div>
                      <div><p className="text-sm font-semibold text-[#0A2540]">{comm.agent?.name || 'Unknown'}</p><p className="text-xs text-[#6B7280]">{comm.agent?.email}</p></div>
                    </div>
                  </td>
                  <td className="px-6 py-4"><p className="text-sm font-medium text-[#0A2540]">{comm.customer?.name || 'Customer'}</p><p className="text-xs text-[#6B7280]">{comm.customer?.mobile}</p></td>
                  <td className="px-6 py-4"><span className="text-sm font-black text-[#0A2540]">{formatCurrency(comm.amount)}</span></td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${comm.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : comm.status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${comm.status === 'paid' ? 'bg-emerald-400' : 'bg-amber-400'}`}></span>
                      {comm.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#6B7280]">{formatDate(comm.earned_at)}</td>
                  <td className="px-6 py-4 text-sm text-[#6B7280]">{comm.payment_reference ? <code className="bg-gray-50 px-2 py-0.5 rounded text-xs font-mono border border-gray-200">{comm.payment_reference}</code> : '—'}</td>
                  <td className="px-6 py-4">
                    {comm.status === 'pending' ? (
                      <button onClick={() => handlePaySingle(comm)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs font-semibold shadow-sm hover:shadow-md transition-all">
                        <CreditCard className="h-3 w-3" /> Pay
                      </button>
                    ) : (
                      <span className="text-xs text-emerald-600 font-medium">Paid {formatDate(comm.paid_at)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment Modal */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#0A2540]" style={{fontFamily:'Outfit, sans-serif'}}>{currentCommission ? "Pay Commission" : "Bulk Pay Commissions"}</h3>
              <button onClick={() => setShowPayModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            <div className="mb-5 p-4 bg-blue-50 border border-blue-100 rounded-xl">
              {currentCommission ? (
                <><p className="text-sm text-[#6B7280]">Agent: <span className="font-semibold text-[#0A2540]">{currentCommission.agent?.name}</span></p><p className="text-sm text-[#6B7280]">Amount: <span className="font-black text-[#1565C0] text-lg">{formatCurrency(currentCommission.amount)}</span></p></>
              ) : (
                <><p className="text-sm text-[#6B7280]">Selected: <span className="font-semibold text-[#0A2540]">{selectedCommissions.length} commissions</span></p><p className="text-sm text-[#6B7280]">Total: <span className="font-black text-[#1565C0] text-lg">{formatCurrency(commissions.filter(c => selectedCommissions.includes(c.id)).reduce((s, c) => s + (c.amount || 0), 0))}</span></p></>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Payment Reference (UTR) *</Label>
                <Input value={paymentRef} onChange={e => setPaymentRef(e.target.value)} placeholder="Enter payment reference" className="mt-1.5 rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0]" />
              </div>
              {currentCommission && (
                <div>
                  <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Notes (Optional)</Label>
                  <textarea value={paymentNotes} onChange={e => setPaymentNotes(e.target.value)} placeholder="Add notes..." className="mt-1.5 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0] focus:outline-none" rows="3" />
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowPayModal(false)} disabled={processing} className="flex-1 rounded-xl h-11">Cancel</Button>
              <Button onClick={confirmPayment} disabled={processing || !paymentRef} className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl h-11 font-semibold shadow-sm hover:shadow-md transition-all">{processing ? 'Processing...' : 'Confirm Payment'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
